<template>
    <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" :class="{'graph-shadow': !expired, 'graph-shadow-expired': expired}">
        <defs>
            <linearGradient id="textGradient" gradientTransform="rotate(90)">
                <stop class="text-top" offset="10%"/>
                <stop class="text-bottom" offset="90%"/>
            </linearGradient>
            <radialGradient id="circleGradientExpired">
                <stop offset="49.9%" class="graph-background" />
                <stop offset="50%" class="graph-internal5" />
                <stop offset="54%" class="graph-internal6" />
                <stop offset="58%" class="graph-internal5" />
                <stop offset="58.1%" class="graph-background" />

                <stop offset="68%" class="graph-background" />
                <stop offset="68.1%" class="graph-internal3" />
                <stop offset="74%" class="graph-internal4" />
                <stop offset="80%" class="graph-internal3" />
                <stop offset="80.1%" class="graph-background" />

                <stop offset="87%" class="graph-background" />
                <stop offset="87.1%" class="graph-expired" />
                <stop offset="92%" class="graph-expired-light" />
                <stop offset="97%" class="graph-expired" />
                <stop offset="97.1%" class="graph-expired-light" />

                <stop offset="98%" class="graph-expired-light"/>
                <stop offset="98.1%" class="graph-expired" />
                <stop offset="100%" class="graph-expired" />
            </radialGradient>
            <radialGradient id="circleGradient">
                <stop offset="49.9%" class="graph-background" />
                <stop offset="50%" class="graph-internal5" />
                <stop offset="54%" class="graph-internal6" />
                <stop offset="58%" class="graph-internal5" />
                <stop offset="58.1%" class="graph-background" />

                <stop offset="68%" class="graph-background"/>
                <stop offset="68.1%" class="graph-internal3" />
                <stop offset="74%" class="graph-internal4" />
                <stop offset="80%" class="graph-internal3" />
                <stop offset="80.1%" class="graph-background"/>

                <stop offset="87%" class="graph-background"/>
                <stop offset="87.1%" class="graph-internal1" />
                <stop offset="92%" class="graph-internal2" />
                <stop offset="97%" class="graph-internal1" />
                <stop offset="97.1%" class="graph-background" />

                <stop offset="98%" class="graph-background"/>
                <stop offset="98.1%" class="graph-external" />
                <stop offset="100%" class="graph-external" />
            </radialGradient>
        </defs>

        <!-- using my radial gradient -->
        <circle cx="100" cy="100" r="100" fill="url('#circleGradientExpired')" v-if="expired" />
        <circle cx="100" cy="100" r="100" fill="url('#circleGradient')" v-else />

        <g fill="currentColor">
            <text v-if="!small && !sorn" y="38%" transform="translate(0, 2)" font-size="11">
                <tspan x="50%" text-anchor="middle" class="graph-text">{{ formattedLabel }}</tspan>
            </text>
            <text y="50%" :transform="sorn ? 'translate(0, 10)' : 'translate(0, 16)'" :font-size="dueLabelFontSize" font-weight="bold" font-family="TradeGothicLT" fill="url('#textGradient')">
                <tspan x="50%" text-anchor="middle" class="graph-text">{{ dueLabel }}</tspan>
            </text>
            <text v-if="!small && !sorn" y="68%" transform="translate(0, 2)" font-size="11">
                <tspan x="50%" text-anchor="middle" class="graph-text">DAYS</tspan>
            </text>
        </g>

        <circle
                v-if="days > 0"
                cx="100"
                cy="100"
                :stroke="graphColor"
                r="74"
                fill="none"
                stroke-width="6%"
                stroke-linecap="round"
                :stroke-dasharray="currentDashArray"
                stroke-dashoffset="116">
        </circle>
    </svg>
</template>

<script lang="ts">
import {defineComponent} from 'vue';

const circleLength = 2 * Math.PI * 74;

export default defineComponent({
    props: {
        label: {type: String, default: ''},
        days: {type: Number, default: 0},
        small: {type: Boolean, default: false},
        expired: {type: Boolean, default: false},
        sorn: {type: Boolean, default: false},
    },
    data() {
        return {
            filledDash: 0,
            emptyDash: circleLength,
        };
    },
    computed: {
        graphColor(): string {
            if (this.days <= 28) {
                return '#d4180b';
            }

            if (this.days <= 90) {
                return '#f26505';
            }

            return '#2ac225';
        },
        dueLabel(): string {
            if (this.sorn) {
                return 'SORN';
            }

            if (this.expired) {
                return '0';
            }

            if (this.days === 0) {
                return '-';
            }

            return this.days.toString();
        },
        dueLabelFontSize(): number {
            if (this.sorn) {
                return 28;
            }

            return 36;
        },
        formattedLabel(): string {
            if (this.label === 'CARTAX') {
                return 'TAX';
            }

            return this.label.toUpperCase();
        },
        finalFilledDash(): [number, number] {
            const percentFilled = this.days / 365;
            const circleFilled = circleLength * percentFilled;
            const circleNotFilled = circleLength - circleFilled;

            return [circleFilled, circleNotFilled];
        },
        currentDashArray(): string {
            return this.filledDash.toFixed(2) + ' ' + this.emptyDash.toFixed(2);
        },
    },
    mounted() {
        this.animateGraph();
    },
    watch: {
        days() {
            this.animateGraph();
        },
    },
    methods: {
        animateGraph() {
            this.filledDash = 0;
            this.emptyDash = circleLength;

            window.requestAnimationFrame(this.animateStep);
        },
        animateStep() {
            const finalFilledDash = this.finalFilledDash;
            const framesPerSecond = 60;
            const speed = finalFilledDash[0] / framesPerSecond;

            this.filledDash = this.filledDash + speed;
            this.emptyDash = this.emptyDash - speed;

            if (this.filledDash < finalFilledDash[0]) {
                window.requestAnimationFrame(this.animateStep);
            }
        },
    },
});
</script>


<style lang="scss">
@use 'sass:color';
@use '../../../sass/variables';

.dark-theme {
  --graph-background: #040a1a;
  --graph-shadow: rgba(18, 84, 203, 0.7);

  --graph-internal1: #1c2a47;
  --graph-internal2: #1254cb;
  --graph-internal3: #7a9ccc;
  --graph-internal4: #fafbfd;
  --graph-internal5: #1c2a47;
  --graph-internal6: #1254cb;
  --graph-external: #364773;

  --text-top: #fff;
  --text-bottom: #d3d2cd;
}

.graph-internal1 {
  stop-color: var(--graph-internal1, #d2dceb);
}

.graph-internal2 {
  stop-color: var(--graph-internal2, #fbfbfb);
}

.graph-internal3 {
  stop-color: var(--graph-internal3, #d2dceb);
}

.graph-internal4 {
  stop-color: var(--graph-internal4, #fbfbfb);
}

.graph-internal5 {
  stop-color: var(--graph-internal5, #34415e);
}

.graph-internal6 {
  stop-color: var(--graph-internal6, #34415e);
}

.graph-external {
  stop-color: var(--graph-external, #969eb2);
}

.graph-background {
  stop-color: var(--graph-background, #fff);
}

.text-top {
  stop-color: var(--text-top, #040a1a);
}

.text-bottom {
  stop-color: var(--text-bottom, #000);
}

.graph-shadow {
  filter: drop-shadow(0 0 10px var(--graph-shadow, rgba(0, 0, 0, 0.4)));
}

.graph-shadow-expired {
  filter: drop-shadow(0 0 10px rgba(255, 0, 0, 0.4));
}

.graph-expired {
  stop-color: variables.$g-stop;
}

.graph-expired-light {
  stop-color: color.adjust(variables.$g-stop, $lightness: 20%);
}
</style>
